import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Prompt } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { faPlus, faTrashAlt, faSave } from '@fortawesome/pro-solid-svg-icons';
import { faCircleNotch } from '@fortawesome/pro-regular-svg-icons';

import { Icon } from 'js/components';
import ConfirmModal from 'js/components/modal/ConfirmModal';
import { useNotify } from 'js/hooks/useNotify';
import { standardCollator } from 'js/utils/string';
import formatRubricSaveError from '../formatRubricSaveError';
import {
  getWeightOverridesMap,
  newWeightSchemeId,
  overrideMapControlledInputValue,
  sanitizeStandardWeightsOnDocument,
  weightOverridesSortedEntries,
} from './rubricWeightsUtils';
import { useRubricWeights } from './RubricWeightsContext';
import {
  RubricQuestionSetWeightRow,
  RubricQuestionWeightRow,
} from 'js/components/RubricWeights';

import styles from 'js/components/RubricWeights/RubricWeights.module.scss';

const UNSAVED_CHANGES_MESSAGE =
  'Hold on - weight changes are not saved yet. Leave anyway?';

function collectDuplicateOverrideNames(entries) {
  const dups = new Set();
  const n = entries.length;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const a = String(entries[i]?.name ?? '').trim();
      const b = String(entries[j]?.name ?? '').trim();
      if (a && b && standardCollator.compare(a, b) === 0) {
        dups.add(a);
      }
    }
  }
  return [...dups];
}

function RubricWeightsComponent({ workspaceId, onSave }) {
  const notify = useNotify();
  const {
    draftDocument,
    committedDocument,
    hasDraftChanges,
    commitSavedSnapshot,
    selectedPanel,
    setSelectedPanel,
    isStandardView,
    selectedOverrideEntry,
    onSetOverrideName,
    onAddWeightOverride,
    onRemoveWeightOverride,
    isSchemeNameTaken,
    getDraftStandardWeightByQuestionSetGuid,
    getCommittedStandardWeightByQuestionSetGuid,
    getDraftStandardWeightByQuestionGuid,
    getCommittedStandardWeightByQuestionGuid,
    standardWeightPlaceholderString,
    onDraftQuestionSetWeightChange,
    onDraftQuestionWeightChange,
    onDraftOverrideWeightChange,
  } = useRubricWeights();

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [newSchemeModalOpen, setNewSchemeModalOpen] = useState(false);
  const [newSchemeName, setNewSchemeName] = useState('');
  const [modalError, setModalError] = useState(null);
  const [confirmDeleteScheme, setConfirmDeleteScheme] = useState(false);

  const overrides = weightOverridesSortedEntries(draftDocument);
  const committedOverrideWeights = isStandardView
    ? null
    : committedDocument.weight_overrides?.[selectedPanel]?.weights;

  const openNewSchemeModal = useCallback(() => {
    setNewSchemeName('');
    setModalError(null);
    setNewSchemeModalOpen(true);
  }, []);

  const toggleNewSchemeModal = useCallback(() => {
    setNewSchemeModalOpen((open) => {
      if (open) setModalError(null);
      return !open;
    });
  }, []);

  const handleConfirmNewScheme = useCallback(() => {
    const trimmed = newSchemeName.trim();
    if (!trimmed) {
      setModalError('Name is required.');
      return;
    }
    if (isSchemeNameTaken(trimmed)) {
      setModalError(
        'A scheme with this name already exists (comparison ignores letter case).'
      );
      return;
    }
    const schemeId = newWeightSchemeId();
    onAddWeightOverride(trimmed, schemeId);
    setSelectedPanel(schemeId);
    setNewSchemeModalOpen(false);
    setNewSchemeName('');
    setModalError(null);
  }, [newSchemeName, isSchemeNameTaken, onAddWeightOverride, setSelectedPanel]);

  const handleSave = useCallback(
    (e) => {
      e.preventDefault();
      setSubmitError(null);

      const dupNames = collectDuplicateOverrideNames(
        weightOverridesSortedEntries(draftDocument)
      );
      if (dupNames.length > 0) {
        const msg = `Each weight scheme needs a unique name (ignoring letter case). Duplicates: ${dupNames.join(
          ', '
        )}`;
        setSubmitError(msg);
        notify({ type: 'danger', message: msg });
        return;
      }

      const entries = weightOverridesSortedEntries(draftDocument);
      const overridesForSave = {};
      for (const { id, name, weights } of entries) {
        const n = String(name ?? '').trim();
        if (!n) continue;
        overridesForSave[id] = {
          name: n,
          weights:
            weights && typeof weights === 'object' && !Array.isArray(weights)
              ? weights
              : {},
        };
      }

      const droppedUnnamed =
        Object.keys(getWeightOverridesMap(draftDocument)).length -
        Object.keys(overridesForSave).length;
      let document = {
        ...draftDocument,
        question_sets: draftDocument.question_sets ?? [],
        weight_overrides: overridesForSave,
      };
      document = sanitizeStandardWeightsOnDocument(document);

      setSubmitting(true);
      onSave({ document })
        .then(() => {
          commitSavedSnapshot(document);
          if (droppedUnnamed > 0) {
            notify({
              type: 'warning',
              message: `${droppedUnnamed} unnamed weight scheme${
                droppedUnnamed === 1 ? '' : 's'
              } were not saved.`,
            });
          }
          notify({ type: 'success', message: 'Weights saved.' });
        })
        .catch((err) => {
          const text = formatRubricSaveError(err);
          setSubmitError(text);
          notify({ type: 'danger', message: text });
        })
        .finally(() => setSubmitting(false));
    },
    [commitSavedSnapshot, draftDocument, notify, onSave]
  );

  useEffect(() => {
    if (!hasDraftChanges || typeof window === 'undefined') {
      return undefined;
    }
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = UNSAVED_CHANGES_MESSAGE;
      return UNSAVED_CHANGES_MESSAGE;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasDraftChanges]);

  const nodeMetaByGuid = useMemo(() => {
    const out = {};
    (draftDocument.question_sets ?? []).forEach((questionSet) => {
      out[questionSet.guid] = {
        nodeType: 'question_set',
        questionSetGuid: questionSet.guid,
      };
      (questionSet.questions ?? []).forEach((question) => {
        out[question.guid] = {
          nodeType: 'question',
          questionSetGuid: questionSet.guid,
          questionGuid: question.guid,
        };
      });
    });
    return out;
  }, [draftDocument.question_sets]);

  const nodeMetaByGuidRef = useRef(nodeMetaByGuid);
  useEffect(() => {
    nodeMetaByGuidRef.current = nodeMetaByGuid;
  }, [nodeMetaByGuid]);

  const handleWeightChange = useCallback(
    (nodeGuid, rawValue) => {
      if (!nodeGuid) return;
      if (isStandardView) {
        const meta = nodeMetaByGuidRef.current[nodeGuid];
        if (!meta) return;
        const parsed = parseFloat(rawValue);
        const nextWeight = Number.isFinite(parsed) ? parsed : 1;
        if (meta.nodeType === 'question_set') {
          onDraftQuestionSetWeightChange(meta.questionSetGuid, nextWeight);
        } else {
          onDraftQuestionWeightChange(
            meta.questionSetGuid,
            meta.questionGuid,
            nextWeight
          );
        }
        return;
      }
      onDraftOverrideWeightChange(nodeGuid, rawValue);
    },
    [
      isStandardView,
      onDraftQuestionSetWeightChange,
      onDraftQuestionWeightChange,
      onDraftOverrideWeightChange,
    ]
  );

  return (
    <div className="container-fluid">
      <Prompt when={hasDraftChanges} message={UNSAVED_CHANGES_MESSAGE} />
      <Modal
        isOpen={newSchemeModalOpen}
        toggle={toggleNewSchemeModal}
        backdrop="static"
      >
        <ModalHeader toggle={toggleNewSchemeModal}>
          New weight scheme
        </ModalHeader>
        <ModalBody>
          <Form
            onSubmit={(ev) => {
              ev.preventDefault();
              handleConfirmNewScheme();
            }}
          >
            <FormGroup>
              <Label for="rubric-new-scheme-name">Scheme name</Label>
              <Input
                id="rubric-new-scheme-name"
                value={newSchemeName}
                onChange={(ev) => {
                  setNewSchemeName(ev.target.value);
                  setModalError(null);
                }}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    ev.preventDefault();
                    handleConfirmNewScheme();
                  }
                }}
                placeholder="e.g. KTLO"
                autoFocus
              />
              {modalError && (
                <p className="text-danger small mt-1 mb-0">{modalError}</p>
              )}
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            type="button"
            onClick={toggleNewSchemeModal}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            type="button"
            onClick={handleConfirmNewScheme}
          >
            Add scheme
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmModal
        isOpen={confirmDeleteScheme}
        title="Delete weight scheme?"
        onConfirm={() => {
          if (selectedPanel !== 'standard') {
            onRemoveWeightOverride(selectedPanel);
            setSelectedPanel('standard');
          }
          setConfirmDeleteScheme(false);
        }}
        onCancel={() => setConfirmDeleteScheme(false)}
        confirmButtonLabel="Delete"
        confirnButtonColor="danger"
      >
        <p>
          Remove scheme <strong>{selectedOverrideEntry?.name}</strong> and all
          of its weight overrides?
        </p>
      </ConfirmModal>

      <Card className="mb-3">
        <CardBody>
          <div className={styles['weights']}>
            <div className={styles.toolbar}>
              {!isStandardView && selectedOverrideEntry ? (
                <FormGroup className={styles['scheme-name-input-group']}>
                  <Input
                    id="rubric-scheme-rename"
                    bsSize="sm"
                    className={styles.renameInput}
                    value={selectedOverrideEntry.name ?? ''}
                    onChange={(ev) =>
                      onSetOverrideName(selectedPanel, ev.target.value)
                    }
                  />
                  <Button
                    size="sm"
                    type="button"
                    color="secondary"
                    onClick={() => setConfirmDeleteScheme(true)}
                  >
                    <Icon icon={faTrashAlt} />
                  </Button>
                </FormGroup>
              ) : (
                <div className={styles['standard-scheme-name']}>
                  Default weights
                </div>
              )}
              <div className={styles['scheme-picker']}>
                <Input
                  id="rubric-weight-scheme-select"
                  type="select"
                  bsSize="sm"
                  className={styles.schemeSelect}
                  value={isStandardView ? 'standard' : String(selectedPanel)}
                  onChange={(ev) => {
                    const v = ev.target.value;
                    if (v === 'standard') {
                      setSelectedPanel('standard');
                      return;
                    }
                    setSelectedPanel(v);
                  }}
                  aria-label="Weight scheme to view or edit"
                >
                  <option value="standard">Default</option>
                  {overrides.map((ov) => (
                    <option key={ov.id} value={ov.id}>
                      {ov.name?.trim()
                        ? ov.name
                        : `Scheme ${ov.id.slice(0, 8)}`}
                    </option>
                  ))}
                </Input>
                <Button
                  color="primary"
                  type="button"
                  size="sm"
                  onClick={openNewSchemeModal}
                >
                  <Icon icon={faPlus} />
                </Button>
              </div>
            </div>

            <div className={styles.grid}>
              <div className={styles['grid-header']}>
                <div className={styles['header-cell']}></div>
                <div className={styles['header-cell']}>Weight</div>
              </div>

              {(draftDocument.question_sets ?? []).map((qs) => (
                <div className="row-stripe-reset" key={qs.guid}>
                  <RubricQuestionSetWeightRow
                    questionSet={qs}
                    isStandardView={isStandardView}
                    draftWeight={
                      isStandardView
                        ? getDraftStandardWeightByQuestionSetGuid(qs.guid)
                        : overrideMapControlledInputValue(
                            selectedOverrideEntry?.weights,
                            qs.guid
                          )
                    }
                    standardWeight={standardWeightPlaceholderString(qs.weight)}
                    committedWeight={
                      isStandardView
                        ? getCommittedStandardWeightByQuestionSetGuid(qs.guid)
                        : overrideMapControlledInputValue(
                            committedOverrideWeights,
                            qs.guid
                          )
                    }
                    onChange={handleWeightChange}
                  />
                  {(qs.questions ?? []).map((q) => (
                    <RubricQuestionWeightRow
                      key={q.guid}
                      questionSet={qs}
                      question={q}
                      isStandardView={isStandardView}
                      draftWeight={
                        isStandardView
                          ? getDraftStandardWeightByQuestionGuid(
                              qs.guid,
                              q.guid
                            )
                          : overrideMapControlledInputValue(
                              selectedOverrideEntry?.weights,
                              q.guid
                            )
                      }
                      standardWeight={standardWeightPlaceholderString(q.weight)}
                      committedWeight={
                        isStandardView
                          ? getCommittedStandardWeightByQuestionGuid(
                              qs.guid,
                              q.guid
                            )
                          : overrideMapControlledInputValue(
                              committedOverrideWeights,
                              q.guid
                            )
                      }
                      onChange={handleWeightChange}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          {submitError && (
            <pre className="text-danger small bg-light p-2 rounded mt-2 mb-0">
              {submitError}
            </pre>
          )}
        </CardBody>
      </Card>
      <div className={styles['save-bar']}>
        <Button
          color="primary"
          type="button"
          disabled={submitting || !hasDraftChanges}
          onClick={handleSave}
        >
          <Icon
            icon={submitting ? faCircleNotch : faSave}
            spin={submitting}
            fixedWidth={true}
            className="icon-left"
          />
          Save weights
        </Button>
      </div>
    </div>
  );
}

export default RubricWeightsComponent;
